import { supabase } from '@/integrations/supabase/client';
import { Company } from '@/lib/types';
import { enrichCompanyWithWebSearch } from './companyWebSearchService';

export const getCompanies = async (searchQuery: string = ''): Promise<Company[]> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      return [];
    }
    
    const { data: isAdminData, error: adminCheckError } = await supabase.rpc('is_admin', {
      user_id: userId
    });
    
    if (adminCheckError) {
      console.error('Error checking admin status:', adminCheckError);
      return [];
    }
    
    const isAdmin = !!isAdminData;
    
    let query = supabase.from('companies').select('*');
    
    if (searchQuery) {
      query = query.ilike('name', `%${searchQuery}%`);
    }
    
    if (!isAdmin) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .single();
      
      if (profileData?.company_id) {
        query = query.eq('id', profileData.company_id);
      } else {
        return [];
      }
    }
    
    const { data, error } = await query.order('name');
    
    if (error) {
      console.error('Error fetching companies:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in getCompanies:', error);
    return [];
  }
};

export const getCompanyById = async (id: string): Promise<Company | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      return null;
    }
    
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('role, company_id')
      .eq('id', userId)
      .single();
    
    if (profileError) {
      console.error('Error fetching profile:', profileError);
      return null;
    }

    const isAdmin = profileData.role === 'admin';
    
    if (!isAdmin && profileData.company_id !== id) {
      console.warn('User does not have access to this company');
      return null;
    }
    
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error('Error fetching company:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error in getCompanyById:', error);
    return null;
  }
};

export const createCompany = async (companyData: { name: string } & Partial<Omit<Company, 'name'>>): Promise<Company> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('companies')
      .insert(companyData)
      .select()
      .single();
    
    if (error) {
      console.error('Error creating company:', error);
      throw error;
    }
    
    await supabase.functions.invoke('manage-user-company', {
      body: {
        action: 'add',
        userId: userId,
        companyId: data.id,
        role: 'admin',
        createdBy: userId
      }
    });
    
    await supabase.rpc('set_company', {
      p_user_id: userId,
      p_company_id: data.id
    });
    
    // Automatically trigger web search enrichment for the new company
    try {
      console.log('Starting automatic web search enrichment for new company:', data.id);
      await enrichCompanyWithWebSearch(data);
    } catch (enrichError) {
      console.error('Failed to start web search enrichment:', enrichError);
      // Don't throw error here - we still want to return the created company
      // The web search can be triggered manually later if needed
    }
    
    return data;
  } catch (error) {
    console.error('Error in createCompany:', error);
    throw error;
  }
};

export const updateCompany = async (id: string, companyData: Partial<Company>): Promise<Company> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data: isAdminData, error: adminCheckError } = await supabase.rpc('is_admin', {
      user_id: userId
    });
    
    if (adminCheckError) {
      console.error('Error checking admin status:', adminCheckError);
      throw new Error('Permission verification failed');
    }
    
    const isAdmin = !!isAdminData;
    
    if (!isAdmin) {
      throw new Error('Only administrators can update companies');
    }
    
    const { data, error } = await supabase
      .from('companies')
      .update(companyData)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating company:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in updateCompany:', error);
    throw error;
  }
};

export const deleteCompany = async (id: string): Promise<void> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data: isAdminData, error: adminCheckError } = await supabase.rpc('is_admin', {
      user_id: userId
    });
    
    if (adminCheckError) {
      console.error('Error checking admin status:', adminCheckError);
      throw new Error('Permission verification failed');
    }
    
    const isAdmin = !!isAdminData;
    
    if (!isAdmin) {
      throw new Error('Only administrators can delete companies');
    }
    
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteCompany:', error);
    throw error;
  }
};
